import { Device } from 'mediasoup-client';
import type { Transport } from 'mediasoup-client/types';
import {
  isDeliberatelyMuted,
  resolveEffectiveMute,
  VOICE,
  zoneAtIn,
  zoneVoiceGainIn,
  LEVELS,
  type ConsumeResult,
  type DoorOpenState,
  type MediaKind,
  type MediaSource,
  type ProduceResult,
  type TransportParams,
  type WorldZone,
} from '@duck/contracts';
import type { RealtimeClient } from './realtime-client';
import { MicPipeline } from './mic-pipeline';
import { isTabHidden } from '../lib/tab-visibility';

/** The local player's current room, surfaced to the HUD zone chip (server still gates audibility). */
export interface CurrentZone {
  id: string;
  name: string;
  isolated: boolean;
}

/** One selectable mic, as offered by the HUD device picker. */
export interface MicDevice {
  deviceId: string;
  label: string;
}

/** Persisted PTT preference (per browser) so the choice survives a refresh. ADR-0009 M3.3. */
const PTT_STORAGE_KEY = 'duck.voice.ptt';

/** Persisted audio-device picks (per browser) so the chosen mic + speaker survive a refresh /
 *  reconnect. We re-pin the saved device on the next session and only fall back to the system default
 *  when it's genuinely gone — see {@link VoiceClient.start} / {@link VoiceClient.applySinkId}. */
const MIC_DEVICE_STORAGE_KEY = 'duck.voice.micDeviceId';
const SPEAKER_DEVICE_STORAGE_KEY = 'duck.voice.speakerDeviceId';

/** Read a saved deviceId (best-effort; private mode / no storage returns null = system default). */
function loadStoredDeviceId(key: string): string | null {
  try {
    if (typeof localStorage !== 'undefined') {
      const v = localStorage.getItem(key);
      if (v && v.length > 0) return v;
    }
  } catch {
    /* storage unavailable — fall through to the system default */
  }
  return null;
}

/** Persist (or clear, when null) the player's explicit device pick. Best-effort. */
function storeDeviceId(key: string, id: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (id) localStorage.setItem(key, id);
    else localStorage.removeItem(key);
  } catch {
    /* storage unavailable — the in-memory choice still holds for this session */
  }
}

/**
 * Lifecycle of the voice subsystem, surfaced to React so the HUD can show an honest mic state.
 * `denied`/`error` are terminal-but-recoverable (the player can retry); nothing here ever crashes
 * the office.
 */
export type VoiceStatus =
  | 'idle' // not started yet
  | 'requesting' // asking the browser for the mic (permission prompt may be showing)
  | 'live' // mic is producing; we hear nearby ducks
  | 'denied' // the player declined the mic permission
  | 'unsupported' // this browser can't do WebRTC/getUserMedia
  | 'error'; // the handshake failed for some other reason

/**
 * Lifecycle of the LOCAL webcam ("show your face", ADR-0030), surfaced to the HUD camera button +
 * self-preview. Independent of {@link VoiceStatus}: the mic and the camera are separate channels on the
 * one media session. `off` is the default and the unremarkable norm — never a state to be nudged out of.
 */
export type CameraStatus =
  | 'off' // not sharing (the default — opt-in, never forced)
  | 'requesting' // asking the browser for the camera (permission prompt may be showing)
  | 'on' // producing webcam video; nearby ducks can see your face
  | 'denied' // the player declined the camera permission
  | 'unsupported' // no camera / this browser can't capture video
  | 'error'; // producing the webcam failed for some other reason

/** Cap the webcam bitrate hard — a head-bubble is tiny, so a modest stream keeps the SFU + CPU cheap. */
const VIDEO_MAX_BITRATE = 200_000;

/**
 * Lifecycle of the LOCAL screen share (Media Hub, ADR-0033), surfaced to the host's "Share screen"
 * button + the "● You're live" tell. Independent of the mic + webcam: a host can talk, show their face,
 * AND share a screen on the one media session. `off` is the default and the unremarkable norm.
 */
export type ScreenStatus =
  | 'off' // not sharing (the default — opt-in, never forced)
  | 'requesting' // the browser screen-picker is open
  | 'on' // producing the screen; everyone in the zone sees it
  | 'denied' // the player dismissed the picker / denied capture
  | 'unsupported' // this browser can't getDisplayMedia
  | 'error'; // producing the screen failed for some other reason

/** A screen share is a full frame (presentation/video), so it gets a far higher cap than a head-bubble. */
const SCREEN_MAX_BITRATE = 2_500_000;

export interface VoiceClientCallbacks {
  /** Fired whenever the lifecycle status changes (drives the HUD mic button). */
  onStatus?: (status: VoiceStatus) => void;
  /** Fired when our own mute state changes (so the HUD reflects it instantly). */
  onMuted?: (muted: boolean) => void;
  /**
   * Fired when my BROADCASTABLE audio-device presence changes — `voiceOn` (voice is live, I can HEAR the
   * room) and `micMuted` (my mic is DELIBERATELY muted). Unlike {@link onMuted}, this reports the manual
   * mute INTENT, never the per-keypress push-to-talk gate, so the participant-list 🎤/🎧 indicators don't
   * flicker while someone holds the talk key. Wired to the realtime client to broadcast to everyone.
   */
  onVoiceState?: (voiceOn: boolean, micMuted: boolean) => void;
  /**
   * Fired (throttled) with the set of session ids currently speaking, INCLUDING ourselves when our
   * mic is live + unmuted and above the level — so the speaking indicator can light up our duck too
   * ("consent through constant awareness", ADR-0009).
   */
  onSpeakingChange?: (speakingSessionIds: Set<string>) => void;
  /**
   * Fired when the local player's room changes (the predicted self position crossed a zone boundary).
   * Drives the HUD zone chip ("which room am I in / is it private"). Client UI only — the SERVER is
   * still the sole authority for who is audible (ADR-0009 M3.2).
   */
  onZoneChange?: (zone: CurrentZone | null) => void;
  /** Fired when push-to-talk is toggled on/off (so the HUD reflects it + can persist it). */
  onPttChange?: (enabled: boolean) => void;
  /** Fired (throttled) with our current mic input level (0..1) for the accessible level meter. */
  onInputLevel?: (level: number) => void;
  /**
   * Fired (throttled, ~vad cadence) with the LOUDNESS (0..1) of everyone currently speaking — keyed by
   * sessionId, INCLUDING ourselves. This is the live voice amplitude (self mic level + each remote
   * peer's received RMS), measured locally, so the world can make a louder voice physically BIGGER on
   * screen (the duck swells with the shout — ADR-0009 "consent through constant awareness", taken
   * further: you can SEE how loud each voice is, not just that it's on). Only speaking sessions appear;
   * a session absent from the map is treated as silent (0). Never transmitted — purely a render cue.
   */
  onSpeakingLevels?: (levels: Map<string, number>) => void;
  /**
   * Fired whenever my LOCAL set of blocked peers changes (M3.4). Self-protection only — the server is
   * the authority that actually severs the audio both ways; this drives the HUD list so the block
   * reflects instantly even after the server closes the consumer and the peer drops from the audible
   * list (the blocked citizen is never shown to or notified — Constitution Art. 1).
   *
   * Keyed by STABLE userId (not sessionId): a blocked peer who reconnects gets a fresh sessionId, so
   * keying by userId keeps the block — and the Unblock control — coherent across their reconnect. The
   * HUD resolves each userId to its CURRENT roster entry (name + live sessionId) to render the list.
   */
  onBlockedChange?: (blockedUserIds: Set<string>) => void;
  /**
   * Fired when the set of peers I currently hear CHANGES (a peer became audible / dropped — M3.4), so
   * the HUD "People nearby" list can refresh. Coarse membership signal only (NOT the per-frame gain),
   * so it never re-renders React on the spatial hot path.
   */
  onAudibleChange?: (audible: AudiblePeer[]) => void;
  /**
   * Fired when the LOCAL camera status changes (drives the HUD camera button + the self-preview). The
   * second arg is the player's OWN outgoing webcam stream when live (so the self-preview shows EXACTLY
   * what nearby ducks see — consent through constant awareness, ADR-0030), else null.
   */
  onCameraStatus?: (status: CameraStatus, selfStream: MediaStream | null) => void;
  /**
   * Fired when a nearby peer's webcam stream arrives (became visible to me) or drops (turned off / left
   * range / blocked). Drives the in-world head bubble and the pinned-camera card. Server-authoritative
   * and proximity-gated exactly like audio: I only ever receive a face the server forwards to me.
   */
  onPeerVideoChange?: (sessionId: string, stream: MediaStream | null) => void;
  /**
   * Fired when the LOCAL screen-share status changes (drives the host's Share-screen button + the live
   * tell). The second arg is the host's OWN outgoing screen stream when live (so a self-preview shows
   * exactly what the room sees — the ADR-0030 consent invariant, applied to the screen), else null.
   */
  onScreenStatus?: (status: ScreenStatus, selfStream: MediaStream | null) => void;
  /**
   * Fired when a screen share from a peer in my zone arrives (I can now see it) or drops (host stopped /
   * left / blocked). Drives the zone's Media Hub screen. Server-authoritative + ZONE-broadcast: I only
   * ever receive a screen the server forwards to me (everyone in the host's zone, sealed from others).
   */
  onScreenShareChange?: (sessionId: string, stream: MediaStream | null) => void;
}

/** One audible peer surfaced to the HUD "People nearby" list (M3.4): who I hear + how loudly. */
export interface AudiblePeer {
  sessionId: string;
  /** The peer's display name, resolved from the room roster (falls back to a friendly placeholder). */
  name: string;
  /** The peer's live spatial gain (0..1) — mirrors what the e2e snapshot reports for that peer. */
  gain: number;
}

/** One remote peer's audio graph node: source → gain → destination, plus its consumer. */
interface PeerAudio {
  sessionId: string;
  consumerId: string;
  producerId: string;
  stream: MediaStream;
  source: MediaStreamAudioSourceNode;
  gain: GainNode;
  /** Hidden <audio> element kept alive so some browsers actually pull the WebRTC track. */
  sink: HTMLAudioElement;
  /**
   * Loudness tap on the RECEIVED stream (off `source`, BEFORE the zone/master gain) so we read the
   * peer's TRUE shout volume — used to swell their duck while they speak. No output edge (analysers
   * don't pass audio), so it never touches what we hear.
   */
  analyser: AnalyserNode;
  analyserBuf: Float32Array<ArrayBuffer>;
}

/** One remote peer's webcam (ADR-0030): the consumed video stream the render layer turns into a bubble. */
interface PeerVideo {
  sessionId: string;
  consumerId: string;
  producerId: string;
  stream: MediaStream;
}

/**
 * Client half of M3.1 voice (ADR-0009, ADR-0057). Owns getUserMedia, the mediasoup-client `Device`,
 * the send/recv transports, our mic producer, and a per-peer WebAudio graph. Voice is flat and
 * global-within-a-zone: every audible peer is at equal, full, centered volume (no distance→volume fade,
 * no stereo pan) — the per-peer gain is just the shared {@link zoneVoiceGainIn} zone gain (1 in-room /
 * commons, 0.5 for a one-hop neighbour through an open door, ADR-0039), times the personal trim.
 *
 * The SERVER decides who is audible: we only consume producers the server tells us about via
 * NEW_PRODUCER, and we drop them on PRODUCER_CLOSED. The client never picks who it may hear.
 */
export class VoiceClient {
  private status: VoiceStatus = 'idle';
  /** Effective mute, as everything downstream (track/producer/server/ring) sees it. */
  private muted = false;
  /** The player's deliberate mute toggle, kept SEPARATE from the PTT gate so toggling PTT off
   * restores it (open-mic unless they had manually muted). The effective {@link muted} above is
   * derived by {@link resolveEffectiveMute}: in PTT mode the talk key gates and this is PARKED;
   * with PTT off, this gates. The two NEVER compose (that OR was the "PTT kills the mic" bug). */
  private manualMuted = false;
  /** Push-to-talk mode. When on, the mic stays muted unless the player holds the talk key. */
  private pttEnabled = false;
  /** True only while the talk key (V) is held — meaningful only when {@link pttEnabled}. */
  private pttTalking = false;
  private started = false;
  private stopped = false;

  /** The room the local player is currently standing in (null = open floor), for the HUD chip. */
  private currentZone: WorldZone | null = null;
  /** Last input level (0..1) sampled from our own mic, mirrored to the accessible level meter. */
  private inputLevel = 0;
  /** The mic deviceId currently in use (or null for the system default). Seeded from the saved pick so
   *  a refresh/reconnect re-acquires the SAME microphone (falling back to default only if it's gone). */
  private currentDeviceId: string | null = loadStoredDeviceId(MIC_DEVICE_STORAGE_KEY);
  /** The audio-OUTPUT deviceId (speaker/headphones) the player picked, or null for the system default.
   *  Routes the whole voice graph via `AudioContext.setSinkId`; restored on every (re)connect. */
  private currentSinkId: string | null = loadStoredDeviceId(SPEAKER_DEVICE_STORAGE_KEY);

  private device: Device | null = null;
  private sendTransport: Transport | null = null;
  private recvTransport: Transport | null = null;
  private micStream: MediaStream | null = null;
  /** The RAW track from getUserMedia. We DON'T produce this directly — it carries keystrokes/room
   *  tone; we route it through {@link micPipeline} and produce the cleaned track instead. The mute
   *  gate flips THIS track's `enabled` (cutting the chain at the source — instant + authoritative). */
  private micTrack: MediaStreamTrack | null = null;
  /** The mic cleanup chain (HPF → LPF → noise gate). Produces the track we actually send to peers and
   *  owns the analyser the self-VAD/level-meter reads (so they reflect the cleaned signal). Rebuilt on
   *  device swap, torn down on stop/reconnect — never leaked across a re-handshake. */
  private micPipeline: MicPipeline | null = null;
  /** The CLEANED track produced to mediasoup (mic-pipeline output). Distinct from {@link micTrack}. */
  private processedTrack: MediaStreamTrack | null = null;
  // The mediasoup Producer/Consumer types are generic; we hold them loosely and trust the SFU API.
  private micProducer: { id: string; close(): void; pause(): void; resume(): void } | null = null;

  // ── Webcam video ("show your face", ADR-0030) — an INDEPENDENT channel on the same send transport ──
  /** Current LOCAL camera lifecycle, surfaced to the HUD camera button + self-preview. */
  private cameraStatus: CameraStatus = 'off';
  /**
   * Does the player WANT their camera on? Like {@link manualMuted}, this DESIRE survives a transparent
   * reconnect (the media teardown nulls the producer/stream, but this flag drives reinitialize to
   * re-acquire the camera). Set true by enableCamera, false by disableCamera / permanent stop.
   */
  private cameraDesired = false;
  /** The raw webcam stream from getUserMedia (video-only; the mic is a separate stream). */
  private cameraStream: MediaStream | null = null;
  /** The webcam video track we produce to the SFU. */
  private cameraTrack: MediaStreamTrack | null = null;
  private cameraProducer:
    | {
        id: string;
        close(): void;
        pause(): void;
        resume(): void;
        rtpParameters?: { codecs?: { mimeType: string }[] };
      }
    | null = null;
  /** Negotiated mimeType of our OUTGOING webcam (e.g. 'video/VP8' or 'video/H264') — for diag + the e2e. */
  private cameraCodec: string | null = null;
  /** Per remote peer's consumed VIDEO (keyed by producerId, mirroring {@link peers} for audio). */
  private readonly videoPeers = new Map<string, PeerVideo>();
  /** Fast sessionId → live remote video stream, for the render layer + pinned card (O(1) reads). */
  private readonly videoBySession = new Map<string, MediaStream>();

  // ── Screen share (Media Hub, ADR-0033) — a host's ZONE-broadcast screen on the same send transport ──
  /** Current LOCAL screen-share lifecycle, surfaced to the host's Share-screen button + the live tell. */
  private screenStatus: ScreenStatus = 'off';
  /** Does the host WANT to share their screen? Unlike the camera this CANNOT be silently re-acquired on a
   *  reconnect (getDisplayMedia always needs a fresh user gesture), so a reconnect ends the share. */
  private screenDesired = false;
  /** The raw display capture from getDisplayMedia (video + optional system/tab audio). */
  private screenStream: MediaStream | null = null;
  // `rtpSender` (the underlying RTCRtpSender) lets us RE-TUNE the screen bitrate live via setParameters
  // (no renegotiation) as the audience grows/shrinks — the adaptive-bitrate path (ADR-0034).
  private screenVideoProducer: { id: string; close(): void; rtpSender?: RTCRtpSender } | null = null;
  /** The screen-share video maxBitrate (bps) currently applied — so we only setParameters on a change. */
  private screenMaxBitrate = 0;
  private screenAudioProducer: { id: string; close(): void } | null = null;
  /** Per remote peer's consumed screen VIDEO (keyed by producerId). */
  private readonly screenPeers = new Map<string, PeerVideo>();
  /** Fast sessionId → live remote screen stream, for the Media Hub card (O(1) reads). */
  private readonly screenBySession = new Map<string, MediaStream>();
  /** Per remote peer's screen AUDIO sink (keyed by producerId) — a plain element on the media volume. */
  private readonly screenAudioEls = new Map<string, { sessionId: string; el: HTMLAudioElement }>();
  /** The player's media-volume preference (0..1) for screen audio (the 'radio' channel). Default full. */
  private mediaVolume = 1;

  private audioCtx: AudioContext | null = null;
  /** Listener-side MASTER gain over every peer (the "Voices nearby" fader). Each peer's chain ends here
   *  instead of at the destination, so this one node scales the WHOLE voice channel while the per-peer
   *  flat zone gain (updateSpatialGains) sets each duck's level underneath. Created in start(),
   *  torn down with the AudioContext; the desired level survives both (instance field below). */
  private masterVoiceGain: GainNode | null = null;
  /** The player's voice-volume preference (0..1). Remembered across reconnects (a fresh handshake rebuilds
   *  the node and re-applies this) so a turned-down voice channel stays turned down. Default full. */
  private desiredVoiceVolume = 1;
  /** Per remote peer (keyed by their producerId, which is what PRODUCER_CLOSED references). */
  private readonly peers = new Map<string, PeerAudio>();
  /** Producers with a consume in flight — guards against the server's self-heal re-announce kicking
   *  off a duplicate concurrent consume. Cleared on completion/failure so a failed attempt retries. */
  private readonly consuming = new Set<string>();
  /** Active-speaker set last reported by the server (drives indicators). */
  private speakers = new Set<string>();
  /**
   * STABLE userIds I've blocked (M3.4). LOCAL & OPTIMISTIC: the SERVER is the security boundary that
   * actually closes the audio stream both ways (a blocked stream is severed server-side, never just
   * muted locally), but the server's PRODUCER_CLOSED then drops the peer from {@link peers} — so this
   * set is what lets the HUD keep showing "blocked" after the audible peer is gone, and lets Unblock
   * find them again.
   *
   * Keyed by userId, NOT sessionId: a blocked citizen who reconnects comes back with a new sessionId,
   * so a sessionId-keyed block would silently go stale (the "Blocked" list couldn't find them to lift
   * it). We resolve sessionId→userId from the roster at block time and store the stable identity; the
   * wire message still carries the current `targetSessionId` (the server keys its own block by user).
   * Ephemeral per room; never persisted, scored, or surfaced to the blocked party (Constitution Art.1).
   */
  private readonly blocked = new Set<string>();

  /**
   * PERSONAL per-peer voice trims (Discord-style): stable userId → gain MULTIPLIER (1 = unchanged, <1
   * quieter, >1 louder), applied OVER the proximity + zone fade in {@link updateSpatialGains}. Keyed by
   * userId (not sessionId) so a trim survives the peer reconnecting with a new session, mirroring
   * {@link blocked}. LOCAL & OUTPUT-ONLY: never transmitted, never changes what anyone else hears
   * (Constitution Art. 1). Absent ⇒ 1 (default). Seeded from the persisted per-device store at client
   * creation and kept in sync by the audio-settings apply hook.
   */
  private readonly peerGains = new Map<string, number>();

  // Local voice-activity detection on our OWN mic, so our duck pulses the instant we speak —
  // independent of whether the server echoes us in ACTIVE_SPEAKERS (never a hidden hot mic). The
  // analyser normally points at the mic-pipeline's FILTERED pre-gate tap (`levelAnalyser`, so the meter is
  // robust + decoupled from the gate); only the rare no-pipeline fallback creates its own source node.
  private selfMicSource: MediaStreamAudioSourceNode | null = null;
  private selfAnalyser: AnalyserNode | null = null;
  private selfAnalyserBuf: Float32Array<ArrayBuffer> | null = null;
  private selfSpeaking = false;

  private spatialTimer: ReturnType<typeof setInterval> | null = null;
  private vadTimer: ReturnType<typeof setInterval> | null = null;
  /** Low-frequency poll of the player's zone — runs from construction (independent of the mic) so the
   * HUD chip is correct even before voice is enabled. */
  private zoneTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly realtime: RealtimeClient,
    private readonly callbacks: VoiceClientCallbacks = {},
  ) {
    // Restore the player's saved PTT preference (best-effort; private mode / no storage is fine).
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem(PTT_STORAGE_KEY) === '1') {
        this.pttEnabled = true;
      }
    } catch {
      /* storage unavailable — default to open-mic */
    }

    // Dev-only observability for the e2e "manual testing agent" (never used by product logic).
    if (typeof window !== 'undefined' && import.meta.env?.DEV) {
      (window as unknown as { __duckVoice?: () => unknown }).__duckVoice = () => this.debugSnapshot();
    }

    // Zone awareness is decoupled from the mic: poll the predicted self position a few times a second
    // and tell the HUD which room we're in (even with voice off). Cheap point-in-rect test.
    this.zoneTimer = setInterval(() => {
      if (isTabHidden()) return; // cost-census: the room chip is visual — skip the zoneAt while backgrounded
      this.pollZone();
    }, 250);
    // Surface the restored PTT preference once a listener can be attached (next tick).
    queueMicrotask(() => {
      if (!this.stopped) this.callbacks.onPttChange?.(this.pttEnabled);
    });
  }

  /**
   * The resolved GEOMETRY level (Phase 3) for the room's zone math — read off the SAME synced `levelId` the
   * engine resolves against, so proximity-voice zones match the world the player sees (office or castle).
   * Falls back to the office (the back-compat default) if the id is unknown.
   */
  private level() {
    return LEVELS[this.realtime.levelId()] ?? LEVELS.office;
  }

  /** Recompute the local player's zone from the predicted self position; emit on a boundary cross. */
  private pollZone(): void {
    const self = this.realtime.self();
    if (!self) return;
    const zone = zoneAtIn(self.x, self.y, this.level().zones);
    if (zone?.id === this.currentZone?.id) return; // no change
    this.currentZone = zone;
    this.callbacks.onZoneChange?.(this.currentZoneInfo());
  }

  /**
   * Dev-only snapshot of the live voice state for e2e assertions. Existing fields (status, mute,
   * per-peer gain, speakers, currentZone, pttEnabled, inputLevel) are UNCHANGED; M3.4 adds:
   *   - `audible`: the peers I currently consume, each with their display name + live gain;
   *   - `blocked`: the CURRENT sessionIds of blocked peers who are PRESENT in the roster. Internally we
   *     track blocks by stable userId now (so they survive a reconnect), but this field still reports
   *     sessionIds — re-resolved from the roster on every read — so existing e2e assertions keep working;
   *   - `block(sessionId)` / `unblock(sessionId)`: drive a block/unblock from the e2e the same way the
   *     HUD does (take a sessionId, resolve userId internally, send the intent + update the optimistic
   *     set). The SERVER still enforces the cut.
   */
  debugSnapshot(): {
    status: VoiceStatus;
    muted: boolean;
    selfSpeaking: boolean;
    speakers: string[];
    peers: { sessionId: string; gain: number }[];
    audible: AudiblePeer[];
    blocked: string[];
    currentZone: CurrentZone | null;
    pttEnabled: boolean;
    inputLevel: number;
    voiceVolume: number;
    /** VAD gate telemetry (ADR-0044): whether the AudioWorklet VAD is active + its latest rms/voicing/gate. */
    vad: { active: boolean; speaking: boolean; rms: number; voicing: number } | null;
    /** Local camera lifecycle (ADR-0030) + the sessionIds whose webcam I currently receive — for e2e. */
    cameraStatus: CameraStatus;
    videoPeers: string[];
    /** Local screen-share lifecycle (ADR-0033) + the sessionIds whose screen I currently receive — e2e. */
    screenStatus: ScreenStatus;
    screenPeers: string[];
    /** Negotiated mimeType of my outgoing webcam ('video/VP8' | 'video/H264'), for the H264 e2e. */
    videoCodec: string | null;
    block: (sessionId: string) => void;
    unblock: (sessionId: string) => void;
    /** Drive the camera from the e2e the same way the HUD button does. */
    enableCamera: () => void;
    disableCamera: () => void;
    /** Drive the screen share from the e2e the same way the host button does. */
    enableScreenshare: () => void;
    disableScreenshare: () => void;
  } {
    return {
      status: this.status,
      muted: this.muted,
      selfSpeaking: this.selfSpeaking,
      speakers: [...this.speakers],
      peers: [...this.peers.values()].map((p) => ({
        sessionId: p.sessionId,
        gain: Math.round(p.gain.gain.value * 1000) / 1000,
      })),
      audible: this.audiblePeers(),
      blocked: this.blockedPresentSessionIds(),
      currentZone: this.currentZone
        ? { id: this.currentZone.id, name: this.currentZone.name, isolated: this.currentZone.isolated }
        : null,
      pttEnabled: this.pttEnabled,
      inputLevel: Math.round(this.inputLevel * 1000) / 1000,
      // The listener-side voice-channel master level (0..1) — lets the e2e assert the fader took effect.
      voiceVolume: Math.round(this.desiredVoiceVolume * 1000) / 1000,
      vad: this.micPipeline
        ? {
            active: this.micPipeline.usingVad,
            speaking: this.micPipeline.speaking,
            rms: Math.round(this.micPipeline.lastRms * 1000) / 1000,
            voicing: Math.round(this.micPipeline.lastVoicing * 1000) / 1000,
          }
        : null,
      cameraStatus: this.cameraStatus,
      videoPeers: [...this.videoBySession.keys()],
      screenStatus: this.screenStatus,
      screenPeers: [...this.screenBySession.keys()],
      videoCodec: this.cameraCodec,
      block: (sessionId: string) => this.blockCitizen(sessionId),
      unblock: (sessionId: string) => this.unblockCitizen(sessionId),
      enableCamera: () => void this.enableCamera(),
      disableCamera: () => this.disableCamera(),
      enableScreenshare: () => void this.enableScreenshare(),
      disableScreenshare: () => this.disableScreenshare(),
    };
  }

  getPttEnabled(): boolean {
    return this.pttEnabled;
  }

  currentZoneInfo(): CurrentZone | null {
    return this.currentZone
      ? { id: this.currentZone.id, name: this.currentZone.name, isolated: this.currentZone.isolated }
      : null;
  }

  getStatus(): VoiceStatus {
    return this.status;
  }

  isMuted(): boolean {
    return this.muted;
  }

  private setStatus(status: VoiceStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.callbacks.onStatus?.(status);
    // Going live ⇒ I can hear the room; any other status ⇒ I can't. Broadcast so everyone's roster
    // 🎧 indicator updates (paired with the 🎤 mute intent below).
    this.emitVoicePresence();
  }

  /**
   * Broadcast my audio-device presence for the participant list's 🎤/🎧 indicators: whether voice is live
   * (I can HEAR) and whether my mic is DELIBERATELY muted. The "muted" flag is the {@link isDeliberatelyMuted}
   * view — NOT the transient PTT gate — so the badge stays steady while push-to-talk is held AND a player
   * who turns a pre-existing mute into push-to-talk reads as an active participant, not stuck "muted".
   * Called on status, manual-mute, AND PTT-mode changes; idempotent enough that the realtime layer can
   * safely send each call.
   */
  private emitVoicePresence(): void {
    const deliberatelyMuted = isDeliberatelyMuted({
      manualMuted: this.manualMuted,
      pttEnabled: this.pttEnabled,
    });
    this.callbacks.onVoiceState?.(this.status === 'live', deliberatelyMuted);
  }

  /**
   * Bring voice up: resume the AudioContext (must follow a user gesture — autoplay policy), get the
   * mic, load the device, build both transports, and start producing. Safe to call once; a second
   * call is a no-op. Failure paths set a `denied`/`unsupported`/`error` status rather than throwing
   * into the caller — the office keeps running without voice.
   *
   * MUST be called from a user gesture handler (e.g. the HUD mic-enable click) so getUserMedia is
   * permitted and the AudioContext is allowed to start.
   */
  async start(): Promise<void> {
    if (this.started || this.stopped) return;
    this.started = true;

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      this.failStart('unsupported');
      return;
    }

    this.setStatus('requesting');

    // Bring up the shared media session first (AudioContext + master fader, device, transports,
    // subscriptions, spatial loop) WITHOUT the mic — the camera shares this exact session, so it lives
    // in ensureMediaConnected. Idempotent + self-healing after a reconnect teardown.
    const connected = await this.ensureMediaConnected();
    if (!connected) {
      this.failStart('error');
      return;
    }
    if (this.stopped) return this.stop();

    try {
      // Mic. Echo cancellation / noise suppression make open-mic proximity pleasant. Honors a deviceId
      // the player picked before enabling voice OR saved from a previous session (the HUD device
      // picker). A denial here is recoverable.
      try {
        this.micStream = await navigator.mediaDevices.getUserMedia({
          audio: this.audioConstraints(),
          video: false,
        });
      } catch (err) {
        // The saved mic is pinned with `{ deviceId: { exact } }`, so a vanished device throws
        // OverconstrainedError. Honor "use it if present, else default": forget the stale pick and
        // retry on the system default rather than failing voice outright.
        const name = err instanceof Error ? err.name : '';
        if (this.currentDeviceId && (name === 'OverconstrainedError' || name === 'NotFoundError')) {
          console.warn('[voice] saved microphone unavailable; using system default', err);
          this.currentDeviceId = null;
          storeDeviceId(MIC_DEVICE_STORAGE_KEY, null);
          this.micStream = await navigator.mediaDevices.getUserMedia({
            audio: this.audioConstraints(),
            video: false,
          });
        } else {
          throw err;
        }
      }
      this.micTrack = this.micStream.getAudioTracks()[0] ?? null;
      if (!this.micTrack) {
        this.failStart('error');
        return;
      }
      // Remember which device we actually got, so the picker can pre-select it.
      this.currentDeviceId = this.micTrack.getSettings?.().deviceId ?? this.currentDeviceId;
    } catch (err) {
      // NotAllowedError / PermissionDeniedError → the player declined. Recoverable: failStart resets
      // `started` so the HUD's "Allow mic" retry actually re-prompts.
      const name = err instanceof Error ? err.name : '';
      this.failStart(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'error');
      return;
    }

    try {
      // Device + transports + subscriptions are already up (ensureMediaConnected). Produce the CLEANED
      // mic track (not the raw mic): the chain (HPF → LPF → noise gate) silences keystrokes + room tone
      // BEFORE the audio leaves us. Degrade to the raw track if the chain can't be built (old browser).
      if (!this.device!.canProduce('audio')) {
        this.failStart('error');
        return;
      }
      const trackToProduce = (await this.buildMicPipeline()) ?? this.micTrack!;
      // buildMicPipeline now awaits the VAD worklet load (ADR-0044) — a real suspension point. If a
      // stop()/reconnect tore the transport down during it, bail cleanly instead of calling produce() on a
      // nulled transport (which would throw a caught-but-logged "handshake failed").
      if (this.stopped || !this.sendTransport) return this.stop();
      const producer = await this.sendTransport.produce({ track: trackToProduce });
      this.micProducer = producer;
      // Honor whatever the EFFECTIVE mute is now (a manual mute toggled before the producer existed,
      // OR push-to-talk being on by default — a fresh PTT session must start silent until V is held).
      // Route through the single mute path so the SERVER producer is paused too (setProducerPaused) —
      // a fresh producer starts UNPAUSED, so without this it could transmit audio the player believes
      // muted (Constitution Art.1 / ADR-0009). `muted` starts false, so the guard lets the first
      // effective-muted application through.
      this.applyEffectiveMute();
      if (this.stopped) return this.stop();

      // Local VAD: read the pipeline's FILTERED, pre-gate tap so the self speaking-ring + level meter
      // track the player's own live (rumble/key-reduced) mic robustly — decoupled from the gate (a
      // post-gate self-tap deadlocks the self-ring shut on borderline-level speech). What PEERS hear
      // stays honest via the gated produced track + the server active-speaker set. No pipeline → raw mic.
      this.bindSelfVadAnalyser();

      // Local self-VAD poll (mic-specific). The spatial gain loop runs in ensureMediaConnected since it
      // fades RECEIVED peers regardless of whether we have a mic. VAD wants a snappier fixed cadence.
      // The same tick also publishes per-voice LOUDNESS so the world can swell a louder duck.
      if (!this.vadTimer)
        this.vadTimer = setInterval(() => {
          // cost-census: the self ring / level-meter / loudness-swell are all visual — skip the ~8Hz RMS
          // work while backgrounded (the outgoing mic is gated by mute + the VAD worklet, not this).
          if (isTabHidden()) return;
          this.pollSelfVad();
          this.emitSpeakLevels();
        }, 120);
      this.updateSpatialGains();

      this.setStatus('live');
    } catch (err) {
      console.error('[voice] handshake failed', err);
      // Recoverable: release the half-built transports/producer and reset `started` so retry works.
      this.failStart('error');
    }
  }

  /**
   * Bring up the SHARED media session WITHOUT acquiring the mic: AudioContext + listener master gain,
   * the mediasoup Device, both transports, the server voice-notification subscriptions, and the spatial
   * gain loop (which fades RECEIVED peers — independent of whether we have a mic). Enabling the mic
   * (start) AND enabling the camera (enableCamera) both call this first. Idempotent — a no-op once the
   * session is up — and self-healing after a reconnect teardown nulls the device/transports. Returns
   * true once connected, false on failure. MUST be called from a user gesture so the AudioContext can
   * start (the mic/camera buttons satisfy this; join-time auto-start rides the recent "Join" click).
   */
  private async ensureMediaConnected(): Promise<boolean> {
    if (this.stopped) return false;
    if (this.device && this.sendTransport && this.recvTransport) return true; // already up

    try {
      // AudioContext — created/resumed under the user gesture so playback is allowed.
      if (!this.audioCtx) {
        this.audioCtx = new (window.AudioContext ??
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      }
      if (this.audioCtx.state === 'suspended') await this.audioCtx.resume();

      // Route the whole graph to the player's saved output device (speaker/headphones). A reconnect
      // rebuilds the AudioContext, so re-applying here keeps voice coming out of the chosen device —
      // falling back to the system default if that device has since disappeared.
      await this.applySinkId();

      // Listener-side master fader for the whole voice channel (every peer routes through it → destination).
      // Seeded from the remembered preference so a reconnect — which rebuilds this node — keeps the level.
      if (!this.masterVoiceGain) {
        this.masterVoiceGain = this.audioCtx.createGain();
        this.masterVoiceGain.gain.value = this.desiredVoiceVolume;
        this.masterVoiceGain.connect(this.audioCtx.destination);
      }

      // Device — load with the room router's RTP capabilities (now advertises video too, ADR-0030).
      if (!this.device) {
        const routerRtpCapabilities = await this.realtime.sendVoiceRpc<Record<string, unknown>>({
          method: 'getRtpCapabilities',
        });
        const device = new Device();
        await device.load({ routerRtpCapabilities: routerRtpCapabilities as never });
        if (this.stopped) return false;
        this.device = device;
      }

      // Transports (send + recv), each wired to RPC its connect/produce back to the server.
      if (!this.sendTransport) this.sendTransport = await this.createTransport('send');
      if (!this.recvTransport) this.recvTransport = await this.createTransport('recv');
      if (this.stopped) return false;

      // Subscribe to server-authoritative voice notifications (drive audio + webcam + screen receive). The
      // NEW_PRODUCER notice carries `kind` + `source`, so onNewProducer wires spatial audio (mic), a face
      // bubble (cam), or the zone's Media Hub screen + media-gain audio (screen).
      this.realtime.onVoiceNewProducer((n) =>
        void this.onNewProducer(n.producerId, n.peerSessionId, n.kind, n.source),
      );
      this.realtime.onVoiceProducerClosed((n) => this.onProducerClosed(n.producerId));
      this.realtime.onVoiceActiveSpeakers((n) => this.onActiveSpeakers(n.sessionIds));

      // Spatial gain loop — fades received peers by distance at the server's gate cadence. No mic dep.
      if (!this.spatialTimer) {
        this.spatialTimer = setInterval(
          () => this.updateSpatialGains(),
          Math.max(1, Math.round(1000 / VOICE.SUBSCRIPTION_HZ)),
        );
      }

      return true;
    } catch (err) {
      console.error('[voice] media connect failed', err);
      return false;
    }
  }

  // ── Webcam: "show your face" (ADR-0030) ─────────────────────────────────────────────────────────

  getCameraStatus(): CameraStatus {
    return this.cameraStatus;
  }

  isCameraOn(): boolean {
    return this.cameraStatus === 'on';
  }

  /** The player's OWN outgoing webcam stream while live (for the self-preview), else null. */
  getLocalCameraStream(): MediaStream | null {
    return this.cameraStream;
  }

  /** A nearby peer's live webcam stream (for the head bubble + pinned card), or null if none. */
  getPeerVideoStream(sessionId: string): MediaStream | null {
    return this.videoBySession.get(sessionId) ?? null;
  }

  /**
   * Turn the local camera ON: ensure the media session, capture a small webcam stream (a separate
   * getUserMedia from the mic — the channels are independent), and produce it. Opt-in and gesture-gated
   * exactly like the mic. Re-entrant (a no-op if already on), and honors the player turning it back off
   * while the browser permission prompt is still open. Failure is graceful — `denied`/`unsupported`/
   * `error` status, never a throw, and the office keeps running.
   */
  async enableCamera(): Promise<void> {
    if (this.stopped) return;
    if (this.cameraDesired && this.cameraProducer) return; // already on
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      this.setCameraStatus('unsupported');
      return;
    }

    this.cameraDesired = true;
    this.setCameraStatus('requesting');

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: this.videoConstraints(), audio: false });
    } catch (err) {
      const name = err instanceof Error ? err.name : '';
      this.cameraDesired = false;
      this.setCameraStatus(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'denied'
          : name === 'NotFoundError' || name === 'OverconstrainedError'
            ? 'unsupported'
            : 'error',
      );
      return;
    }

    const track = stream.getVideoTracks()[0] ?? null;
    // The player may have turned the camera back off (or we got torn down) while the prompt was open —
    // honor that and don't go live behind their back.
    if (!track || !this.cameraDesired || this.stopped) {
      for (const t of stream.getTracks()) t.stop();
      if (track && !this.stopped) this.setCameraStatus('off');
      return;
    }

    try {
      const connected = await this.ensureMediaConnected();
      if (!connected || this.stopped || !this.cameraDesired) {
        for (const t of stream.getTracks()) t.stop();
        if (!this.stopped && this.cameraDesired) {
          this.cameraDesired = false;
          this.setCameraStatus('error');
        }
        return;
      }
      if (!this.device!.canProduce('video')) {
        for (const t of stream.getTracks()) t.stop();
        this.cameraDesired = false;
        this.setCameraStatus('unsupported');
        return;
      }

      this.cameraStream = stream;
      this.cameraTrack = track;
      // If the OS/camera stops the track (device unplugged, OS privacy toggle), turn ourselves off so the
      // UI + peers stay honest — never a frozen face after the hardware is gone.
      track.addEventListener('ended', () => {
        if (this.cameraDesired) this.disableCamera();
      });

      // Optionally FORCE a specific video codec (e.g. `?videoCodec=h264`) so an e2e can prove the H264
      // path Safari uses, in Chromium. Prod leaves it unset → mediasoup negotiates VP8 first.
      const forced = this.preferredVideoCodec();
      this.cameraProducer = await this.sendTransport!.produce({
        track,
        encodings: [{ maxBitrate: VIDEO_MAX_BITRATE }],
        appData: { kind: 'webcam' },
        ...(forced ? { codec: forced as never } : {}),
      });
      this.cameraCodec = this.cameraProducer.rtpParameters?.codecs?.[0]?.mimeType ?? null;
      // Raced with a turn-off during produce → undo cleanly.
      if (this.stopped || !this.cameraDesired) {
        this.disableCamera();
        return;
      }
      this.setCameraStatus('on');
    } catch (err) {
      console.error('[voice] camera produce failed', err);
      for (const t of stream.getTracks()) t.stop();
      this.cameraStream = null;
      this.cameraTrack = null;
      this.cameraDesired = false;
      this.setCameraStatus('error');
    }
  }

  /**
   * Turn the local camera OFF — the instant, no-confirm kill switch (ADR-0030). Stops the hardware
   * capture (the OS camera light goes dark) and asks the server to CLOSE the producer, so every nearby
   * listener's consumer fires `producerclose` and the face bubble disappears at once (closing the local
   * producer alone doesn't propagate). Idempotent.
   */
  disableCamera(): void {
    this.cameraDesired = false;
    const producerId = this.cameraProducer?.id;
    try {
      this.cameraProducer?.close();
    } catch {
      /* already closed */
    }
    this.cameraProducer = null;
    this.cameraCodec = null;
    if (producerId) {
      void this.realtime
        .sendVoiceRpc({ method: 'closeProducer', producerId })
        .catch(() => undefined);
    }
    if (this.cameraTrack) {
      try {
        this.cameraTrack.stop();
      } catch {
        /* already stopped */
      }
      this.cameraTrack = null;
    }
    if (this.cameraStream) {
      for (const t of this.cameraStream.getTracks()) t.stop();
      this.cameraStream = null;
    }
    this.setCameraStatus('off');
  }

  toggleCamera(): void {
    if (this.cameraStatus === 'on' || this.cameraStatus === 'requesting') this.disableCamera();
    else void this.enableCamera();
  }

  private setCameraStatus(status: CameraStatus): void {
    if (this.cameraStatus === status) return;
    this.cameraStatus = status;
    this.callbacks.onCameraStatus?.(status, this.cameraStream);
  }

  // ── Screen share (Media Hub, ADR-0033) ───────────────────────────────────────────────────────────

  getScreenStatus(): ScreenStatus {
    return this.screenStatus;
  }

  isScreenSharing(): boolean {
    return this.screenStatus === 'on';
  }

  /** The host's OWN outgoing screen stream while live (for a self-preview), else null. */
  getLocalScreenStream(): MediaStream | null {
    return this.screenStream;
  }

  /** A peer's live screen-share stream (for the Media Hub card), or null if none in my zone. */
  getPeerScreenStream(sessionId: string): MediaStream | null {
    return this.screenBySession.get(sessionId) ?? null;
  }

  /** Apply the media-volume preference (0..1) to every incoming screen-audio element. */
  setMediaGain(volume: number): void {
    this.mediaVolume = Math.max(0, Math.min(1, volume));
    for (const { el } of this.screenAudioEls.values()) el.volume = this.mediaVolume;
  }

  /**
   * Re-tune the OUTGOING screen-share bitrate LIVE as the audience changes (ADR-0034 adaptive bitrate).
   * Uses the underlying RTCRtpSender.setParameters — no renegotiation, no track replace, so the viewers'
   * streams are uninterrupted. No-op when not sharing, when the sender isn't exposed, or when unchanged
   * (a tiny tolerance avoids churn on jitter). Best-effort: a failed setParameters never breaks the share.
   */
  setScreenMaxBitrate(bps: number): void {
    const target = Math.max(100_000, Math.min(SCREEN_MAX_BITRATE, Math.round(bps)));
    const sender = this.screenVideoProducer?.rtpSender;
    if (!sender || !this.screenDesired) return;
    if (Math.abs(target - this.screenMaxBitrate) < 50_000) return; // already ~there
    try {
      const params = sender.getParameters();
      if (!params.encodings || params.encodings.length === 0) params.encodings = [{}];
      params.encodings[0]!.maxBitrate = target;
      void sender.setParameters(params).catch(() => undefined);
      this.screenMaxBitrate = target;
    } catch {
      /* setParameters unsupported / racing a close — keep the current encoding */
    }
  }

  /**
   * Begin a screen share (ADR-0033): open the browser screen-picker (getDisplayMedia — always user-
   * gesture-gated + shows an OS picker, never a sneaky capture), then produce the video (+ system/tab
   * audio when present) to the SFU as a `screen`-source producer, which the server ZONE-broadcasts to
   * everyone in the host's room. Re-entrant; graceful on cancel/deny (status only, never a throw).
   */
  async enableScreenshare(initialMaxBitrate: number = SCREEN_MAX_BITRATE): Promise<void> {
    if (this.stopped) return;
    if (this.screenDesired && this.screenVideoProducer) return; // already sharing
    const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
    if (!md?.getDisplayMedia) {
      this.setScreenStatus('unsupported');
      return;
    }

    this.screenDesired = true;
    this.setScreenStatus('requesting');

    let stream: MediaStream;
    try {
      // `audio: true` asks for tab/system sound (the user's pick — chosen earlier in the questions). The
      // browser may still return video-only; we handle either.
      stream = await md.getDisplayMedia({ video: true, audio: true });
    } catch (err) {
      const name = err instanceof Error ? err.name : '';
      this.screenDesired = false;
      this.setScreenStatus(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'error');
      return;
    }

    const videoTrack = stream.getVideoTracks()[0] ?? null;
    // Cancelled the picker, or we got torn down / turned off while it was open → don't go live behind their back.
    if (!videoTrack || !this.screenDesired || this.stopped) {
      for (const t of stream.getTracks()) t.stop();
      if (videoTrack && !this.stopped) this.setScreenStatus('off');
      else if (!this.stopped) this.setScreenStatus('off');
      return;
    }

    try {
      const connected = await this.ensureMediaConnected();
      if (!connected || this.stopped || !this.screenDesired || !this.device!.canProduce('video')) {
        for (const t of stream.getTracks()) t.stop();
        if (!this.stopped && this.screenDesired) {
          this.screenDesired = false;
          this.setScreenStatus(connected ? 'error' : 'error');
        }
        return;
      }

      this.screenStream = stream;
      // The browser's own "Stop sharing" affordance ends the video track → mirror it into our kill switch.
      videoTrack.addEventListener('ended', () => {
        if (this.screenDesired) this.disableScreenshare();
      });

      const startBitrate = Math.max(1, Math.min(SCREEN_MAX_BITRATE, Math.round(initialMaxBitrate)));
      this.screenVideoProducer = await this.sendTransport!.produce({
        track: videoTrack,
        encodings: [{ maxBitrate: startBitrate }],
        appData: { source: 'screen' },
      });
      this.screenMaxBitrate = startBitrate;
      // Pipe screen audio too, when the OS/browser gave us a track (the host opted into sound).
      const audioTrack = stream.getAudioTracks()[0] ?? null;
      if (audioTrack && !this.stopped && this.screenDesired) {
        this.screenAudioProducer = await this.sendTransport!.produce({
          track: audioTrack,
          appData: { source: 'screen' },
        });
      }

      if (this.stopped || !this.screenDesired) {
        this.disableScreenshare();
        return;
      }
      this.setScreenStatus('on');
    } catch (err) {
      console.error('[voice] screen share produce failed', err);
      for (const t of stream.getTracks()) t.stop();
      this.screenStream = null;
      this.screenDesired = false;
      this.setScreenStatus('error');
    }
  }

  /**
   * Stop the screen share — the instant kill switch (ADR-0033, mirroring the camera's). Closes both
   * producers server-side (so every viewer's consumer fires `producerclose` and the screen vanishes at
   * once) and stops the local capture (the browser's "sharing" chrome goes away). Idempotent.
   */
  disableScreenshare(): void {
    this.screenDesired = false;
    for (const p of [this.screenVideoProducer, this.screenAudioProducer]) {
      const producerId = p?.id;
      try {
        p?.close();
      } catch {
        /* already closed */
      }
      if (producerId) {
        void this.realtime.sendVoiceRpc({ method: 'closeProducer', producerId }).catch(() => undefined);
      }
    }
    this.screenVideoProducer = null;
    this.screenAudioProducer = null;
    if (this.screenStream) {
      for (const t of this.screenStream.getTracks()) t.stop();
      this.screenStream = null;
    }
    this.setScreenStatus('off');
  }

  toggleScreenshare(): void {
    if (this.screenStatus === 'on' || this.screenStatus === 'requesting') this.disableScreenshare();
    else void this.enableScreenshare();
  }

  private setScreenStatus(status: ScreenStatus): void {
    if (this.screenStatus === status) return;
    this.screenStatus = status;
    this.callbacks.onScreenStatus?.(status, this.screenStream);
  }

  /**
   * If the page asks for a specific video codec (URL `?videoCodec=h264` or `window.__duckForceVideoCodec`),
   * resolve it from the loaded device's capabilities so `produce()` can pin it. Used by the H264 e2e to
   * prove the codec path Safari uses (Chromium can do H264 too); unset in prod → mediasoup picks VP8 first.
   */
  private preferredVideoCodec(): { mimeType: string } | undefined {
    if (!this.device) return undefined;
    let want: string | null = null;
    try {
      want =
        new URLSearchParams(window.location.search).get('videoCodec') ||
        (window as unknown as { __duckForceVideoCodec?: string }).__duckForceVideoCodec ||
        null;
    } catch {
      want = null;
    }
    if (!want) return undefined;
    const mime = `video/${want}`.toLowerCase();
    const caps = (this.device.rtpCapabilities.codecs ?? []) as { mimeType: string }[];
    return caps.find((c) => c.mimeType.toLowerCase() === mime);
  }

  /**
   * getUserMedia VIDEO constraints. A head-bubble is tiny, so we request a small, square-ish, low
   * frame-rate feed: cheaper bandwidth + CPU + OS-camera scrutiny, and it gently sidesteps the social
   * pressure of hyper-HD faces. `facingMode: 'user'` prefers the front camera on multi-camera devices.
   */
  private videoConstraints(): MediaTrackConstraints {
    return {
      width: { ideal: 320 },
      height: { ideal: 320 },
      frameRate: { ideal: 15, max: 24 },
      facingMode: 'user',
    };
  }

  /**
   * Re-establish voice after the room socket transparently reconnected into the SAME seat (M3.5b).
   *
   * The room socket survived (Colyseus reused it), but the SFU tore OUR media down when the server saw
   * the disconnect — the device, both transports, our producer, and every peer consumer are stale and
   * point at server-side objects that no longer exist. So we release them (the RECOVERABLE teardown,
   * NOT permanent {@link stop}) and re-run the full handshake from scratch: new device, send+recv
   * transports, producer, and consumers (the server re-announces audible peers via NEW_PRODUCER).
   *
   * Re-entrant + idempotent-by-state:
   *   • If voice was never enabled (`!started`) or is permanently stopped, this is a no-op — a reconnect
   *     must never silently turn a mic on that the player never enabled (Constitution Art.1).
   *   • Mute / push-to-talk / chosen-mic / blocked state are INSTANCE fields untouched by the teardown,
   *     so they carry across the reconnect. We force `muted=false` before re-handshaking so the single
   *     mute path re-applies the EFFECTIVE mute to the fresh (unpaused) producer — a player who was
   *     muted/PTT comes back muted, never with a hot mic behind a muted UI.
   *
   * Failure is graceful: a re-handshake that can't complete lands on the recoverable `error`/`denied`
   * status (the HUD's "Retry voice" still works) — the office keeps running.
   */
  async reinitialize(): Promise<void> {
    if (this.stopped) return; // permanent teardown already ran — nothing to restore
    // Nothing was enabled (no mic AND no camera) → do NOT auto-enable anything on reconnect.
    if (!this.started && !this.cameraDesired) return;

    // Capture what was on BEFORE the teardown clears the live handles (the DESIRE flags survive it).
    const wasMic = this.started;
    const wasCamera = this.cameraDesired;

    // Release the stale media graph WITHOUT marking us permanently stopped, so it can be rebuilt.
    this.releaseResources();
    // Clear any lit speaking rings on the world (releaseResources empties the speaker set but does not
    // notify the engine) — matches stop()/failStart(), so stale rings don't linger across the re-handshake.
    this.callbacks.onSpeakingChange?.(new Set());
    this.callbacks.onSpeakingLevels?.(new Map());
    // Force the mute guard to re-apply the effective mute to the fresh producer (releaseResources leaves
    // `muted` as-is; a fresh producer is unpaused, so without this a muted player could come back
    // transmitting — never a hidden hot mic).
    this.started = false;
    this.muted = false;
    this.selfSpeaking = false;

    if (wasMic) {
      // Re-acquire the mic + rebuild device/transports/producer/consumers, honoring the preserved
      // manualMuted/pttEnabled/currentDeviceId. start() sets status back to 'live' (or a recoverable
      // failure) on its own.
      await this.start();
    } else {
      // Camera-only session (mic was never on): rebuild just the media layer (receive path), no mic.
      await this.ensureMediaConnected();
    }
    // Re-acquire the camera if it was on. releaseResources tore down its producer/stream but kept the
    // DESIRE, so a brief off→on flicker on the button during reconnect is honest — it WAS briefly off.
    if (wasCamera) await this.enableCamera();
  }

  /** Create + RPC-wire a send or recv transport. The `connect`/`produce` events ride the room RPC. */
  private async createTransport(direction: 'send' | 'recv'): Promise<Transport> {
    const params = await this.realtime.sendVoiceRpc<TransportParams>({
      method: 'createTransport',
      direction,
    });
    const options = params as unknown as Parameters<Device['createSendTransport']>[0];
    const device = this.device!;
    const transport =
      direction === 'send' ? device.createSendTransport(options) : device.createRecvTransport(options);

    // Both directions: finish ICE+DTLS by handing our DTLS params to the server.
    transport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this.realtime
        .sendVoiceRpc({
          method: 'connectTransport',
          transportId: transport.id,
          dtlsParameters: dtlsParameters as never,
        })
        .then(() => callback())
        .catch((e: Error) => errback(e));
    });

    // Send transport only: the first produce() asks the server to create the server-side producer. We
    // MUST forward `appData.source` ('mic'|'cam'|'screen') so the server classifies the producer
    // correctly — without it a SCREEN share is mis-inferred from `kind` (audio→mic, video→cam), which (a)
    // makes the screen-audio REPLACE the host's mic in the 'audio' slot (mic goes silent) and (b) routes
    // the screen-video through the proximity webcam path instead of the zone-broadcast Media Hub screen
    // (viewers get a black card). See ADR-0033/0034.
    if (direction === 'send') {
      transport.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
        const source = (appData as { source?: MediaSource } | undefined)?.source;
        this.realtime
          .sendVoiceRpc<ProduceResult>({
            method: 'produce',
            transportId: transport.id,
            kind,
            ...(source ? { source } : {}),
            rtpParameters: rtpParameters as never,
          })
          .then((res) => callback({ id: res.id }))
          .catch((e: Error) => errback(e));
      });
    }

    return transport;
  }

  /**
   * A peer became audible (server told us). Consume their producer, resume it, and wire its track
   * into our spatial graph keyed to that peer's session id. Server-authoritative: we only ever
   * consume producers the server announces.
   */
  private async onNewProducer(
    producerId: string,
    peerSessionId: string,
    kind: MediaKind,
    source?: MediaSource,
  ): Promise<void> {
    if (this.stopped || !this.recvTransport || !this.device) return;
    const isScreen = source === 'screen';
    // Spatial VOICE (mic) needs the audio graph; a webcam, a screen-video, and screen-AUDIO (which plays
    // through a plain element on the media volume) do not.
    if (kind === 'audio' && !isScreen && !this.audioCtx) return;
    // Already consuming this producer (any channel), or a consume is in flight → skip.
    if (
      this.peers.has(producerId) ||
      this.videoPeers.has(producerId) ||
      this.screenPeers.has(producerId) ||
      this.screenAudioEls.has(producerId) ||
      this.consuming.has(producerId)
    ) {
      return;
    }
    this.consuming.add(producerId);

    try {
      const result = await this.realtime.sendVoiceRpc<ConsumeResult>({
        method: 'consume',
        producerId,
        rtpCapabilities: this.device.rtpCapabilities as never,
      });
      if (this.stopped) return;

      // mediasoup-client adopts the server-provided `id` as the local Consumer's id (verified for
      // mediasoup-client@3.21.0: Transport.consume constructs `new Consumer({ id, ... })`), so
      // `consumer.id === result.id`. resumeConsumer below sends that id back and the server matches it.
      const consumer = await this.recvTransport.consume({
        id: result.id,
        producerId: result.producerId,
        kind: result.kind,
        rtpParameters: result.rtpParameters as never,
      });

      // Consumers are created paused server-side; resume once we're ready to receive.
      await this.realtime.sendVoiceRpc({ method: 'resumeConsumer', consumerId: consumer.id });
      consumer.resume();
      if (this.stopped) {
        consumer.close();
        return;
      }

      // Branch on source/kind: a SCREEN share (zone-broadcast) drives the Media Hub screen + media-gain
      // audio; otherwise a voice track joins the spatial graph and a webcam track becomes a face bubble.
      if (isScreen) {
        if (result.kind === 'video') {
          this.attachPeerScreenVideo(peerSessionId, producerId, consumer.id, consumer.track);
        } else {
          this.attachPeerScreenAudio(peerSessionId, producerId, consumer.track);
        }
      } else if (result.kind === 'video') {
        this.attachPeerVideo(peerSessionId, producerId, consumer.id, consumer.track);
      } else {
        this.attachPeerAudio(peerSessionId, producerId, consumer.id, consumer.track);
      }
    } catch {
      // A consume can race with a close (a zone flap while walking in) → the producer stays audible,
      // so the server's self-heal re-announces next tick and we retry. Expected, transient, recovers.
    } finally {
      this.consuming.delete(producerId);
    }
  }

  /** Store one peer's webcam stream keyed by producerId + sessionId, and notify the render layer. */
  private attachPeerVideo(
    sessionId: string,
    producerId: string,
    consumerId: string,
    track: MediaStreamTrack,
  ): void {
    const stream = new MediaStream([track]);
    this.videoPeers.set(producerId, { sessionId, consumerId, producerId, stream });
    this.videoBySession.set(sessionId, stream);
    // A nearby face appeared → the engine attaches it to that duck's head bubble; the pinned card (if
    // this peer is pinned) starts rendering it. Proximity-gated + ephemeral, exactly like their voice.
    this.callbacks.onPeerVideoChange?.(sessionId, stream);
  }

  /** Build the WebAudio graph for one peer's track and store it keyed by producerId. */
  private attachPeerAudio(
    sessionId: string,
    producerId: string,
    consumerId: string,
    track: MediaStreamTrack,
  ): void {
    const ctx = this.audioCtx!;
    const stream = new MediaStream([track]);

    const source = ctx.createMediaStreamSource(stream);
    const gain = ctx.createGain();
    gain.gain.value = 0; // start silent; the spatial loop ramps it to the correct zone gain
    // Terminate the peer chain at the channel master fader (falls back to the destination if, defensively,
    // the master node isn't up yet) — so the "Voices nearby" volume scales every peer at once.
    const out: AudioNode = this.masterVoiceGain ?? ctx.destination;
    source.connect(gain).connect(out);

    // Loudness analyser on the RAW received source (a dead-end branch — no output edge — so it reads the
    // peer's amplitude without affecting playback). Drives the "shout makes the duck bigger" swell.
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const analyserBuf = new Float32Array(new ArrayBuffer(analyser.fftSize * 4));

    // Some browsers won't pull a WebRTC MediaStreamTrack through WebAudio unless it's also attached
    // to a media element. Keep a muted, hidden sink alive (we route real audio via WebAudio).
    const sink = new Audio();
    sink.srcObject = stream;
    sink.muted = true;
    sink.autoplay = true;
    void sink.play().catch(() => undefined);

    this.peers.set(producerId, {
      sessionId,
      consumerId,
      producerId,
      stream,
      source,
      gain,
      sink,
      analyser,
      analyserBuf,
    });
    this.updateSpatialGains();
    // A new peer is audible → refresh the HUD "People nearby" list (membership change, not per-frame).
    this.callbacks.onAudibleChange?.(this.audiblePeers());
  }

  /** A producer we were consuming is gone (peer left range/zone/disconnected/BLOCKED) — tear it down. */
  private onProducerClosed(producerId: string): void {
    const peer = this.peers.get(producerId);
    if (peer) {
      this.peers.delete(producerId);
      this.disposePeer(peer);
      // A peer dropped (incl. the server severing a block) → refresh the HUD "People nearby" list.
      this.callbacks.onAudibleChange?.(this.audiblePeers());
      return;
    }
    // Webcam producer gone (camera off / left range / blocked) → drop the stream + clear the bubble.
    const vid = this.videoPeers.get(producerId);
    if (vid) {
      this.videoPeers.delete(producerId);
      // Only clear the session→stream lookup if it still points at THIS stream (defensive vs. a re-add).
      if (this.videoBySession.get(vid.sessionId) === vid.stream) this.videoBySession.delete(vid.sessionId);
      for (const t of vid.stream.getTracks()) t.stop();
      this.callbacks.onPeerVideoChange?.(vid.sessionId, null);
      return;
    }
    // Screen-share VIDEO gone (host stopped / left zone / blocked) → clear the Media Hub screen.
    const scr = this.screenPeers.get(producerId);
    if (scr) {
      this.screenPeers.delete(producerId);
      if (this.screenBySession.get(scr.sessionId) === scr.stream) this.screenBySession.delete(scr.sessionId);
      for (const t of scr.stream.getTracks()) t.stop();
      this.callbacks.onScreenShareChange?.(scr.sessionId, null);
      return;
    }
    // Screen-share AUDIO gone → tear down its element.
    const sAudio = this.screenAudioEls.get(producerId);
    if (sAudio) {
      this.screenAudioEls.delete(producerId);
      sAudio.el.pause();
      sAudio.el.srcObject = null;
    }
  }

  /** Store one peer's screen-share VIDEO keyed by producerId + sessionId, and notify the Media Hub card. */
  private attachPeerScreenVideo(
    sessionId: string,
    producerId: string,
    consumerId: string,
    track: MediaStreamTrack,
  ): void {
    const stream = new MediaStream([track]);
    this.screenPeers.set(producerId, { sessionId, consumerId, producerId, stream });
    this.screenBySession.set(sessionId, stream);
    this.callbacks.onScreenShareChange?.(sessionId, stream);
  }

  /** Play one peer's screen AUDIO through a plain element on the media volume (NOT spatialized, never a
   *  "speaker"). A screen's sound belongs to the room's shared media, not the proximity voice mix. */
  private attachPeerScreenAudio(
    sessionId: string,
    producerId: string,
    track: MediaStreamTrack,
  ): void {
    const el = new Audio();
    el.autoplay = true;
    el.srcObject = new MediaStream([track]);
    el.volume = this.mediaVolume;
    // Honor the player's chosen output device for screen audio too (best-effort; default if unset/gone).
    this.routeElementToSink(el);
    void el.play().catch(() => undefined); // a later user gesture unlocks it if autoplay is blocked
    this.screenAudioEls.set(producerId, { sessionId, el });
  }

  private disposePeer(peer: PeerAudio): void {
    try {
      peer.source.disconnect();
      peer.gain.disconnect();
      peer.analyser.disconnect();
    } catch {
      /* nodes may already be detached */
    }
    peer.sink.pause();
    peer.sink.srcObject = null;
    for (const t of peer.stream.getTracks()) t.stop();
  }

  /** Server-authoritative active-speaker set → indicators (these are the REMOTE peers we hear). */
  private onActiveSpeakers(sessionIds: string[]): void {
    this.speakers = new Set(sessionIds);
    this.emitSpeaking();
  }

  /**
   * Sample our own mic level and flip the self speaking-ring on/off with a little hysteresis so it
   * doesn't flicker on word gaps. Muted ⇒ never speaking. Cheap RMS over the time-domain buffer.
   */
  /**
   * One on-demand RMS read of the self analyser (the pre-gate, filtered mic tap), or null when the
   * mic graph isn't up. Shared by the ~8 Hz meter poll and any caller that needs "the mic level right
   * NOW" (the Bark Battle samples this at its own 10 Hz cadence during a battle).
   */
  private readSelfRms(): number | null {
    const analyser = this.selfAnalyser;
    const buf = this.selfAnalyserBuf;
    if (!analyser || !buf) return null;
    analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i]! * buf[i]!;
    return Math.sqrt(sum / buf.length);
  }

  /**
   * The CURRENT mic loudness on the same normalized 0..1 scale the HUD meter shows (≈0.3 RMS reads
   * as full). Muted (or no live mic) reads 0 — a muted duck can't be heard, so it can't score a bark
   * either. A fresh analyser read each call (no smoothing), so a 10 Hz game sampler sees real peaks.
   */
  readMicLevelNow(): number {
    if (this.muted) return 0;
    const rms = this.readSelfRms();
    return rms === null ? 0 : Math.min(1, rms / 0.3);
  }

  private pollSelfVad(): void {
    const rms = this.readSelfRms();
    if (rms === null) return;

    // The pre-gate (filtered) RMS, so the accessible level meter shows the player their live mic
    // level even when muted (a reassuring "yes, the mic hears you" — the meter is local, never transmitted).
    // Normalize to a friendly 0..1 bar (≈0.3 RMS reads as full); muted reads as 0 (nothing leaves us).
    const level = this.muted ? 0 : Math.min(1, rms / 0.3);
    if (Math.abs(level - this.inputLevel) > 0.01 || (level === 0 && this.inputLevel !== 0)) {
      this.inputLevel = level;
      this.callbacks.onInputLevel?.(level);
    }

    let speaking = false;
    if (!this.muted && this.status === 'live') {
      // Hysteresis: louder threshold to start, lower to keep, so brief pauses don't drop the ring.
      speaking = this.selfSpeaking ? rms > 0.012 : rms > 0.02;
      // When the VAD worklet gate is active (ADR-0044), only light the own-duck ring while the gate is
      // actually OPEN — a whisper/creak clears the rms self-VAD but the gate may keep it silent, so a lit
      // ring would lie ("registered as speaking ⇒ audible"). The pre-gate level meter above stays honest.
      if (this.micPipeline?.usingVad && !this.micPipeline.speaking) speaking = false;
    }
    if (speaking !== this.selfSpeaking) {
      this.selfSpeaking = speaking;
      this.emitSpeaking();
    }
  }

  /**
   * Publish the union of: remote speakers the server reported + ourselves (when our local VAD says
   * we're talking). Self is always derived from real mic level — consent through constant awareness
   * (ADR-0009): the player can never be speaking without seeing their own duck light up.
   */
  private emitSpeaking(): void {
    const speaking = new Set(this.speakers);
    if (this.selfSpeaking) speaking.add(this.realtime.selfSessionId);
    else speaking.delete(this.realtime.selfSessionId);
    this.callbacks.onSpeakingChange?.(speaking);
  }

  /**
   * Publish the live LOUDNESS (0..1) of everyone currently speaking, so the world can swell a louder
   * voice into a bigger duck. Self uses our own mic level; each remote SPEAKING peer is measured from
   * its received-stream analyser (true shout volume, pre-distance-gain). Only speaking sessions are
   * included — a silent session is simply absent (the renderer treats absent as 0 and shrinks back).
   * Cheap: a few RMS passes over small (512-sample) buffers at the VAD cadence.
   */
  private emitSpeakLevels(): void {
    const cb = this.callbacks.onSpeakingLevels;
    if (!cb) return;
    const levels = new Map<string, number>();
    if (this.selfSpeaking) levels.set(this.realtime.selfSessionId, this.inputLevel);
    for (const peer of this.peers.values()) {
      // Only the server's authoritative speakers swell — so a quiet, unheard tail never twitches a duck.
      if (!this.speakers.has(peer.sessionId)) continue;
      peer.analyser.getFloatTimeDomainData(peer.analyserBuf);
      const buf = peer.analyserBuf;
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i]! * buf[i]!;
      const rms = Math.sqrt(sum / buf.length);
      // Normalize the received amplitude to a friendly 0..1 (received voice is AGC'd + a touch hotter
      // than the raw mic meter, so ~0.22 RMS reads as a full-throated shout).
      levels.set(peer.sessionId, Math.min(1, rms / 0.22));
    }
    cb(levels);
  }

  /**
   * Per-peer voice gain (ADR-0057): FLAT and zone-based — every audible peer is at equal, full, centered
   * volume, with no distance→volume fade and no stereo pan. The gain is just the shared
   * {@link zoneVoiceGainIn} zone gain (1 in-room / commons, 0.5 for a one-hop neighbour through an open
   * door, ADR-0039), times the personal per-listener trim. Still re-run on the ~5Hz spatial timer because
   * zone membership + door state change as ducks walk between rooms.
   */
  private updateSpatialGains(): void {
    if (!this.audioCtx) return;
    // cost-census: nothing to update when there are no consumed peers — skip the self lookup, the per-call
    // `doors` Set allocation, and the empty loop (the 5Hz spatial timer fires even when alone).
    if (this.peers.size === 0) return;
    const self = this.realtime.self();
    const now = this.audioCtx.currentTime;
    // Live door state (ADR-0039), read ONCE for the whole peer pass: the neighbour-voice tier opens through
    // an OPEN doorway and re-seals through a closed one, so the client fade must mirror the door state the
    // server gated on. Built from the SAME synced room state the collision prediction reads.
    const doors: DoorOpenState = {
      closedDoorwayIds: new Set(this.realtime.closedDoorIds()),
      entryDoorOpen: this.realtime.entryDoorOpen(),
    };
    for (const peer of this.peers.values()) {
      const other = this.realtime.players.get(peer.sessionId);
      let gain = 0;
      if (self && other) {
        // Mirror the server's composed zone gate (ADR-0039, ADR-0057): same room / commons ⇒ full volume
        // (gain 1); a one-hop neighbour through an OPEN door ⇒ the fixed 0.5 glimpse. There is NO distance
        // fade — audibility is zone-only, so any forwarded peer sits at their flat zone gain. (`audible:false`
        // can't happen for a forwarded peer — the server never sends a producer we shouldn't hear — so we
        // only apply the zone gain, never re-gate audibility.)
        const level = this.level();
        const selfZone = zoneAtIn(self.x, self.y, level.zones);
        const peerZone = zoneAtIn(other.x, other.y, level.zones);
        gain = zoneVoiceGainIn(level, selfZone, peerZone, doors).gain;
      }
      // PERSONAL per-listener trim (Discord-style, output-only): nudge THIS specific duck quieter/louder on
      // top of the zone gain, keyed by their stable userId so it survives their reconnect. Absent ⇒ 1
      // (unchanged). Never leaves this device; never touched by the server.
      if (other) {
        const mul = this.peerGains.get(other.userId);
        if (mul !== undefined) gain *= mul;
      }
      // Short ramp avoids zipper noise as ducks move between zones / toggle doors.
      peer.gain.gain.setTargetAtTime(gain, now, 0.08);
    }
  }

  /**
   * Unstick spatial-audio playback. When voice auto-starts on join (no fresh user gesture), the
   * AudioContext can come up `suspended` under the browser autoplay policy — peers would be silent
   * until a tick. Call this from the first real user gesture (click / keypress) to resume it. Safe
   * to call repeatedly and before start() (no-op until the context exists).
   */
  resumeAudio(): void {
    if (this.audioCtx && this.audioCtx.state === 'suspended') void this.audioCtx.resume().catch(() => undefined);
  }

  /**
   * Listener-side master volume for the whole voice channel (the "Voices nearby" fader) — a 0..1 scale
   * over every nearby duck at once (0 = silenced, 1 = full). The per-peer flat zone gain still applies
   * underneath (each audible duck at equal volume); this just sets the channel's overall level. This is
   * OUTPUT only — it never touches our mic / what we transmit (that's {@link mute}). Remembered across reconnects
   * (the field outlives the node), and a no-op-safe ramp if voice isn't live yet — applied when start()
   * builds the node. Driven by the per-device audio mix.
   */
  setVoiceVolume(value: number): void {
    this.desiredVoiceVolume = Math.max(0, Math.min(1, value));
    const node = this.masterVoiceGain;
    if (node && this.audioCtx) {
      try {
        node.gain.setTargetAtTime(this.desiredVoiceVolume, this.audioCtx.currentTime, 0.05);
      } catch {
        node.gain.value = this.desiredVoiceVolume;
      }
    }
  }

  /**
   * Replace the PERSONAL per-peer voice trims (stable userId → gain multiplier, 1 = unchanged) and re-apply
   * them at once so dragging a participant's slider is HEARD immediately — the 5 Hz spatial loop would
   * otherwise lag the change up to ~200 ms. Only meaningful multipliers are retained (finite and ≠ 1) so an
   * absent peer costs nothing. Output-only: never touches our mic or what anyone else hears. Seeded from the
   * persisted per-device {@link import('../state/peer-volume.store').usePeerVolumeStore} at client creation
   * and re-pushed by the apply hook whenever the store changes. Safe before start() (the map is retained and
   * the no-op spatial pass applies it once voice is live).
   */
  setPeerGains(gains: Readonly<Record<string, number>>): void {
    this.peerGains.clear();
    for (const [userId, mul] of Object.entries(gains)) {
      if (userId && Number.isFinite(mul) && mul !== 1) this.peerGains.set(userId, mul);
    }
    this.updateSpatialGains();
  }

  // These three set the DELIBERATE mute INTENT (`manualMuted`). While PTT mode is engaged that intent is
  // PARKED (the V key gates the mic, see resolveEffectiveMute) — so calling them mid-PTT only changes what
  // happens once PTT turns off, never the live gate. The HUD only surfaces the mute toggle in open-mic
  // mode for exactly this reason (no silent latch behind the PTT gate); any future caller should know the
  // same. Do NOT reintroduce composing manual mute with the PTT gate — that was the "PTT kills the mic" bug.

  /** Mute: pause our producer server-side AND disable the track locally (instant + obvious). */
  mute(): void {
    this.manualMuted = true;
    this.applyEffectiveMute();
    this.emitVoicePresence();
  }

  /** Unmute: resume our producer and re-enable the local track. */
  unmute(): void {
    this.manualMuted = false;
    this.applyEffectiveMute();
    this.emitVoicePresence();
  }

  toggleMute(): void {
    this.manualMuted = !this.manualMuted;
    this.applyEffectiveMute();
    this.emitVoicePresence();
  }

  // ── Block a citizen's voice (ADR-0009 M3.4, emotion: Trust) ─────────────────────────────────────

  /**
   * Block a specific citizen's voice (self-protection, never punishment). The HUD (and e2e) hand us a
   * sessionId; we resolve it to the peer's STABLE userId via the roster and record THAT optimistically —
   * so the block survives the peer reconnecting with a new sessionId — AND tell the server (which is the
   * real security boundary: it severs the audio both ways, keys its own block by user, and sends
   * PRODUCER_CLOSED, dropping them from {@link peers}). The optimistic set keeps the "blocked" UI
   * coherent after that drop and lets us find them to unblock. The wire message still carries the
   * CURRENT sessionId. Idempotent; no-op for our own session. Never logs or shames them (Constitution
   * Art. 1).
   */
  blockCitizen(sessionId: string): void {
    if (!sessionId || sessionId === this.realtime.selfSessionId) return;
    const userId = this.userIdForSession(sessionId);
    if (!userId) return; // unknown session (roster race / already gone) — nothing to block
    if (this.blocked.has(userId)) return;
    this.blocked.add(userId);
    this.realtime.sendBlockCitizen(sessionId);
    this.emitBlocked();
  }

  /**
   * Undo a block (M3.4). Callers pass a sessionId (the peer's CURRENT session, resolved by the HUD from
   * the blocked userId → roster). We clear the block by its stable userId and ask the server to restore
   * the audio link both ways, carrying that current sessionId on the wire. If the peer is OFFLINE we
   * have no current session to send, so the HUD disables Unblock entirely — but we still guard here so a
   * stray call can never desync (the block stays until they're back and a real session exists).
   */
  unblockCitizen(sessionId: string): void {
    if (!sessionId) return;
    const userId = this.userIdForSession(sessionId);
    if (!userId || !this.blocked.has(userId)) return;
    this.blocked.delete(userId);
    this.realtime.sendUnblockCitizen(sessionId);
    this.emitBlocked();
  }

  /** A snapshot of the STABLE userIds I've blocked (M3.4) — the HUD maps these to current roster rows. */
  getBlocked(): Set<string> {
    return new Set(this.blocked);
  }

  /** Is this session's USER currently blocked by me? (Resolves sessionId→userId via the roster.) */
  isBlocked(sessionId: string): boolean {
    const userId = this.userIdForSession(sessionId);
    return userId != null && this.blocked.has(userId);
  }

  /** Resolve a sessionId to its stable userId via the current roster (null if not present). */
  private userIdForSession(sessionId: string): string | null {
    return this.realtime.players.get(sessionId)?.userId ?? null;
  }

  /**
   * The CURRENT sessionIds of blocked peers who are PRESENT in the roster (a blocked userId maps to a
   * live session). Offline blocked peers contribute nothing here — they have no current session. This
   * is what the e2e snapshot's `blocked` reports (it always spoke in sessionIds), kept honest across a
   * blocked peer's reconnect because the userId→session mapping is re-resolved on every read.
   */
  private blockedPresentSessionIds(): string[] {
    const present: string[] = [];
    for (const [sid, p] of this.realtime.players) {
      if (this.blocked.has(p.userId)) present.push(sid);
    }
    return present;
  }

  private emitBlocked(): void {
    this.callbacks.onBlockedChange?.(new Set(this.blocked));
  }

  /**
   * The peers I currently HEAR (consume), each with their display name + live spatial gain — the
   * "People nearby" list (M3.4). Resolved from the room roster; the SERVER decides who is in this list
   * (we only ever consume producers it announces). A peer I've just blocked may briefly still appear
   * here until the server's PRODUCER_CLOSED lands; the HUD filters blocked ids out so the UI is honest.
   */
  audiblePeers(): AudiblePeer[] {
    return [...this.peers.values()].map((p) => ({
      sessionId: p.sessionId,
      name: this.realtime.players.get(p.sessionId)?.displayName ?? 'A nearby duck',
      gain: Math.round(p.gain.gain.value * 1000) / 1000,
    }));
  }

  // ── Push-to-talk (ADR-0009 M3.3) ────────────────────────────────────────────────────────────────

  /**
   * Toggle push-to-talk mode. ON ⇒ the mic stays muted until the player holds the talk key (V);
   * OFF ⇒ back to open-mic (unmuted unless they had deliberately muted). The deliberate mute toggle
   * is preserved across the switch. Choice is persisted per browser.
   */
  setPttEnabled(enabled: boolean): void {
    if (this.pttEnabled === enabled) return;
    this.pttEnabled = enabled;
    if (!enabled) this.pttTalking = false; // leaving PTT clears any held-talk latch
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(PTT_STORAGE_KEY, enabled ? '1' : '0');
      }
    } catch {
      /* storage unavailable — preference just won't persist */
    }
    this.callbacks.onPttChange?.(enabled);
    this.applyEffectiveMute();
    // Entering/leaving PTT changes the roster's "deliberately muted" view (a PTT player reads as an
    // active participant, not muted) — re-broadcast so the participant list never strands a stale badge.
    this.emitVoicePresence();
  }

  togglePtt(): void {
    this.setPttEnabled(!this.pttEnabled);
  }

  /**
   * Hold/release the push-to-talk key. No-op unless PTT mode is on. The HUD calls these from V
   * keydown/keyup (auto-repeat already filtered there); the effective mute follows instantly via the
   * SAME path as a manual mute (track gate + producer pause/resume + setProducerPaused RPC).
   */
  setPttTalking(talking: boolean): void {
    if (!this.pttEnabled || this.pttTalking === talking) return;
    this.pttTalking = talking;
    this.applyEffectiveMute();
  }

  /**
   * Resolve the deliberate mute toggle and the PTT gate into one EFFECTIVE mute (exactly one gate active
   * at a time — see {@link resolveEffectiveMute}, the shared, unit-tested primitive that encodes WHY the
   * gates must not compose: the old `manualMuted OR pttGate` was the "PTT kills the mic" bug), then drive
   * it down the existing single mute path.
   */
  private applyEffectiveMute(): void {
    const muted = resolveEffectiveMute({
      manualMuted: this.manualMuted,
      pttEnabled: this.pttEnabled,
      pttTalking: this.pttTalking,
    });
    if (this.muted === muted) return;
    this.muted = muted;
    // Local track gate is instant and authoritative for the player's own privacy. Gate BOTH ends of
    // the chain: the raw mic (cuts the pipeline at the source) AND the produced/cleaned track (what
    // mediasoup actually reads) — so the mute and the noise gate compose without one re-opening the
    // other. The noise gate only affects level WITHIN an unmuted track; mute always wins.
    if (this.micTrack) this.micTrack.enabled = !muted;
    if (this.processedTrack) this.processedTrack.enabled = !muted;
    // cost-census: stop the VAD worklet's speech-DSP while muted (the producer is paused below anyway).
    this.micPipeline?.setMuted(muted);
    if (this.micProducer) {
      if (muted) this.micProducer.pause();
      else this.micProducer.resume();
      // Tell the server too (it stops/forwards accordingly).
      void this.realtime
        .sendVoiceRpc({
          method: 'setProducerPaused',
          producerId: this.micProducer.id,
          paused: muted,
        })
        .catch((e: Error) => console.warn('[voice] setProducerPaused failed', e));
    }
    // Muting must instantly drop our own speaking ring — no lag behind the mute.
    if (muted) this.selfSpeaking = false;
    this.callbacks.onMuted?.(muted);
    this.emitSpeaking();
  }

  // ── Mic cleanup chain (voice-quality v1: HPF → LPF → noise gate) ─────────────────────────────────

  /**
   * Build the mic cleanup chain over the current raw {@link micStream} and return the CLEANED track to
   * produce. Best-effort: any failure (ancient browser, no AudioContext) returns null and the caller
   * produces the raw track so voice still works — the cleanup is an enhancement, never a hard dep.
   * Replaces any prior pipeline (so a device swap can't stack two chains / leak nodes).
   */
  private async buildMicPipeline(): Promise<MediaStreamTrack | null> {
    if (!this.audioCtx || !this.micStream) return null;
    // Drop any existing chain first (idempotent rebuild — used by start() AND device swap).
    this.micPipeline?.destroy();
    this.micPipeline = null;
    this.processedTrack = null;
    try {
      const pipeline = await MicPipeline.create(this.audioCtx, this.micStream);
      this.micPipeline = pipeline;
      this.processedTrack = pipeline.outputTrack;
      // The cleaned output track inherits the player's effective mute immediately (a producer built
      // from a muted-but-enabled track would transmit) — the mute gate flips the RAW track below, but
      // mirror it on the output track too so the gate composes with mute without fighting.
      pipeline.outputTrack.enabled = !this.muted;
      return pipeline.outputTrack;
    } catch (e) {
      console.warn('[voice] mic cleanup chain unavailable; producing raw mic', e);
      this.micPipeline = null;
      this.processedTrack = null;
      return null;
    }
  }

  /**
   * Point the self-VAD analyser at the right tap: the pipeline's FILTERED, PRE-gate analyser
   * (`levelAnalyser`) when the chain is up — so the level meter shows the player's live (rumble/key-reduced)
   * mic robustly, decoupled from the gate (a post-gate self-tap would deadlock the meter/ring shut on
   * borderline speech). The speaking RING is separately reconciled to the actual gate state in
   * `pollSelfVad` (ADR-0044). Else a raw-mic analyser fallback. Tears down any prior fallback source node
   * first so we never leak/stack nodes across a rebuild. Best-effort — a failure just leaves the meter at 0.
   */
  private bindSelfVadAnalyser(): void {
    // Release a previously-created FALLBACK source node (the pipeline owns its own analyser; we never
    // disconnect that here — destroy() handles it).
    try {
      this.selfMicSource?.disconnect();
    } catch {
      /* already detached */
    }
    this.selfMicSource = null;
    this.selfAnalyser = null;
    this.selfAnalyserBuf = null;

    if (this.micPipeline) {
      // Reuse the pipeline's FILTERED, pre-gate analyser — robust self level/VAD, decoupled from the
      // gate (see MicPipeline.levelAnalyser doc). No extra node.
      const analyser = this.micPipeline.levelAnalyser;
      this.selfAnalyser = analyser;
      this.selfAnalyserBuf = new Float32Array(new ArrayBuffer(analyser.fftSize * 4));
      return;
    }

    // Fallback (no pipeline): tap the raw mic so the meter still works.
    if (!this.audioCtx || !this.micStream) return;
    try {
      const ctx = this.audioCtx;
      const micSource = ctx.createMediaStreamSource(this.micStream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      micSource.connect(analyser); // analyser has no output edge → nothing reaches the speakers
      this.selfMicSource = micSource;
      this.selfAnalyser = analyser;
      this.selfAnalyserBuf = new Float32Array(new ArrayBuffer(analyser.fftSize * 4));
    } catch {
      /* analyser is best-effort; the server set still drives remote indicators */
    }
  }

  // ── Microphone device selection (ADR-0009 M3.3 accessibility) ───────────────────────────────────

  /**
   * getUserMedia audio constraints, pinning the chosen deviceId if the player picked one.
   *
   * The browser's OWN DSP is our first, zero-cost line against keystrokes + background noise, so we
   * ask for all of it explicitly: `noiseSuppression` (Chrome's WebRTC NS kills a lot of steady room
   * tone + impulsive key clicks), `echoCancellation` (also gates speaker bleed so we don't re-emit
   * peers), `autoGainControl` (keeps quiet talkers audible without manual gain). `channelCount: 1`
   * forces mono — voice is mono, and it halves the bytes on the wire (one channel to encode/forward
   * for every duck in a 10+ session). We stack our own filter+gate chain (mic-pipeline) on top of this.
   */
  private audioConstraints(): MediaTrackConstraints {
    const base: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    };
    if (this.currentDeviceId) base.deviceId = { exact: this.currentDeviceId };
    return base;
  }

  /** The deviceId we last acquired (or null for the system default), so the picker can pre-select. */
  getCurrentDeviceId(): string | null {
    return this.currentDeviceId;
  }

  /** Can this browser route WebAudio output to a chosen device? Chrome/Edge: yes (`AudioContext.setSinkId`,
   *  Chrome 110+). Firefox/Safari: no — the HUD then hides the speaker picker (output stays on default). */
  static supportsSpeakerSelection(): boolean {
    return (
      typeof AudioContext !== 'undefined' &&
      typeof (AudioContext.prototype as { setSinkId?: unknown }).setSinkId === 'function'
    );
  }

  /** The output deviceId currently selected (or null for the system default), so the picker pre-selects. */
  getCurrentSinkId(): string | null {
    return this.currentSinkId;
  }

  /**
   * List the available audio OUTPUT devices (speakers / headphones) for the HUD picker. Like mics,
   * labels only populate after a media-permission grant. Returns [] when output selection is
   * unsupported (Firefox/Safari) or enumeration is blocked, so the picker simply hides itself.
   */
  async listSpeakers(): Promise<MicDevice[]> {
    if (!VoiceClient.supportsSpeakerSelection()) return [];
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return [];
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices
        .filter((d) => d.kind === 'audiooutput')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Speaker ${i + 1}` }));
    } catch {
      return [];
    }
  }

  /**
   * Switch where voice plays back (speaker / headphones). Stored + applied immediately to the live
   * AudioContext and any screen-audio elements. Pre-`live` (no AudioContext yet) it just records the
   * choice — {@link ensureMediaConnected} re-applies it when the context comes up.
   */
  async setSpeaker(deviceId: string): Promise<void> {
    this.currentSinkId = deviceId || null;
    storeDeviceId(SPEAKER_DEVICE_STORAGE_KEY, this.currentSinkId);
    await this.applySinkId();
  }

  /**
   * Route all voice output to {@link currentSinkId}: the spatial-voice AudioContext graph (via
   * `AudioContext.setSinkId`) plus every screen-audio element. If the saved device is gone the browser
   * rejects — we then forget the stale pick and fall back to the system default so audio is never lost.
   */
  private async applySinkId(): Promise<void> {
    const ctx = this.audioCtx as
      | (AudioContext & { setSinkId?: (id: string) => Promise<void> })
      | null;
    if (ctx?.setSinkId) {
      try {
        await ctx.setSinkId(this.currentSinkId ?? '');
      } catch (e) {
        if (this.currentSinkId) {
          console.warn('[voice] saved speaker unavailable; using system default', e);
          this.currentSinkId = null;
          storeDeviceId(SPEAKER_DEVICE_STORAGE_KEY, null);
          try {
            await ctx.setSinkId('');
          } catch {
            /* default sink should always exist; nothing more we can do */
          }
        }
      }
    }
    // Screen-share audio plays through plain <audio> elements (not the WebAudio graph) → route them too.
    for (const { el } of this.screenAudioEls.values()) this.routeElementToSink(el);
  }

  /** Point one <audio> element at the chosen output device (best-effort). Shared by screen-audio attach
   *  and {@link applySinkId} so the cast + null→default coercion live in exactly one place. */
  private routeElementToSink(el: HTMLAudioElement): void {
    const withSink = el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
    if (withSink.setSinkId) void withSink.setSinkId(this.currentSinkId ?? '').catch(() => undefined);
  }

  /**
   * List the available microphones for the HUD picker. Labels are only populated AFTER a mic
   * permission grant (browser privacy), so we fall back to a generic name pre-permission. Returns []
   * gracefully if enumeration is unsupported or blocked — the picker just hides itself.
   */
  async listMicrophones(): Promise<MicDevice[]> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return [];
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices
        .filter((d) => d.kind === 'audioinput')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${i + 1}` }));
    } catch {
      return [];
    }
  }

  /**
   * Switch the active microphone to `deviceId` and hot-swap the producer's track without rebuilding
   * the transport. Prefers `producer.replaceTrack({ track })` (mediasoup-client supports it); falls
   * back to stop+reproduce if not. Also rebuilds the self-VAD analyser so the level meter follows the
   * new mic. Pre-`live` (mic not yet acquired) it just records the choice for the next start().
   */
  async setMicrophone(deviceId: string): Promise<void> {
    this.currentDeviceId = deviceId || null;
    // Remember the explicit pick so it survives a refresh / reconnect (restored in start()).
    storeDeviceId(MIC_DEVICE_STORAGE_KEY, this.currentDeviceId);
    // Not live yet → the choice is honored when start() acquires the mic.
    if (this.status !== 'live' || !this.audioCtx) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: this.audioConstraints(), video: false });
    } catch (e) {
      console.warn('[voice] could not switch microphone', e);
      return;
    }
    const newTrack = stream.getAudioTracks()[0] ?? null;
    if (!newTrack) {
      for (const t of stream.getTracks()) t.stop();
      return;
    }
    // Honor the current effective mute on the freshly acquired RAW track immediately.
    newTrack.enabled = !this.muted;

    // Build a FRESH cleanup chain over the new mic and produce its cleaned track — never the raw one.
    // Keep the OLD pipeline alive until the swap succeeds (so a failed swap leaves voice untouched).
    // We can't reuse buildMicPipeline() here (it destroys the live chain up front); we build a local
    // pipeline against the new stream, swap, and only then retire the old one — gapless + leak-free.
    const oldPipeline = this.micPipeline;
    let newPipeline: MicPipeline | null = null;
    let trackToProduce = newTrack;
    try {
      newPipeline = await MicPipeline.create(this.audioCtx, stream);
      newPipeline.outputTrack.enabled = !this.muted; // mirror the effective mute onto the cleaned track
      trackToProduce = newPipeline.outputTrack;
    } catch (e) {
      console.warn('[voice] mic cleanup chain unavailable for new mic; producing raw', e);
      newPipeline = null;
      trackToProduce = newTrack;
    }

    // Swap into the producer (replaceTrack keeps the same SSRC / consumers intact when available).
    const producer = this.micProducer as unknown as
      | { replaceTrack?: (opts: { track: MediaStreamTrack }) => Promise<void> }
      | null;
    try {
      if (producer?.replaceTrack) {
        await producer.replaceTrack({ track: trackToProduce });
      } else {
        // Fallback: re-produce. Rare; keeps voice working on clients without replaceTrack. A FRESH
        // server-side producer starts UNPAUSED, so when the player is muted we MUST guarantee the
        // server stops it before we trust the mute (Constitution Art.1 / ADR-0009). Unlike the
        // open-mic case, we AWAIT the pause RPC and, on failure, close the hot producer + surface a
        // clear error rather than leave a server mic the player can't hear themselves muting.
        this.micProducer?.close();
        const reproduced = await this.sendTransport!.produce({ track: trackToProduce });
        this.micProducer = reproduced;
        if (this.muted) {
          reproduced.pause();
          try {
            await this.realtime.sendVoiceRpc({
              method: 'setProducerPaused',
              producerId: reproduced.id,
              paused: true,
            });
          } catch (e) {
            // Could not confirm the server paused this fresh producer → it may be transmitting audio
            // the player believes muted. Tear it down so there is NEVER a hot mic behind a muted UI.
            console.error('[voice] could not pause re-produced mic on the server; closing it to avoid a hot mic', e);
            try {
              reproduced.close();
            } catch {
              /* already closed */
            }
            this.micProducer = null;
            newPipeline?.destroy();
            for (const t of stream.getTracks()) t.stop();
            return;
          }
        }
      }
    } catch (e) {
      console.warn('[voice] replaceTrack failed; keeping old mic', e);
      newPipeline?.destroy();
      for (const t of stream.getTracks()) t.stop();
      return;
    }

    // Swap succeeded → adopt the new mic + pipeline, retire the old ones (incl. tearing down the old
    // chain so its nodes/output track don't leak), then rebind the VAD analyser to the new chain.
    const oldStream = this.micStream;
    const oldTrack = this.micTrack;
    this.micStream = stream;
    this.micTrack = newTrack;
    this.micPipeline = newPipeline;
    this.processedTrack = newPipeline?.outputTrack ?? null;
    this.currentDeviceId = newTrack.getSettings?.().deviceId ?? this.currentDeviceId;
    oldPipeline?.destroy(); // stops the old detector loop + old output track; disconnects its nodes
    if (oldTrack) oldTrack.stop();
    if (oldStream) for (const t of oldStream.getTracks()) t.stop();

    // Rebind the self-VAD analyser to the new chain's filtered pre-gate tap (or the raw fallback).
    // Best-effort: the mic switch above already SUCCEEDED, so this never aborts it.
    this.bindSelfVadAnalyser();
  }

  /**
   * Release ALL audio/transport resources (timers, analyser, peers, producer, transports, mic,
   * AudioContext) without deciding whether this is permanent. Shared by {@link stop} (permanent) and
   * {@link failStart} (recoverable) so a failed handshake never leaks the transports it created.
   */
  private releaseResources(): void {
    if (this.spatialTimer) clearInterval(this.spatialTimer);
    this.spatialTimer = null;
    if (this.vadTimer) clearInterval(this.vadTimer);
    this.vadTimer = null;
    // Tear down the mic cleanup chain (stops its detector loop, disconnects every node, stops the
    // produced output track) — so NO AudioContext nodes/tracks leak across a re-handshake (M3.5b).
    this.micPipeline?.destroy();
    this.micPipeline = null;
    this.processedTrack = null;
    try {
      this.selfMicSource?.disconnect();
    } catch {
      /* already detached */
    }
    try {
      this.selfAnalyser?.disconnect();
    } catch {
      /* already detached */
    }
    this.selfMicSource = null;
    this.selfAnalyser = null;
    this.selfAnalyserBuf = null;
    this.selfSpeaking = false;
    // The mic is gone → the level meter must read zero (analyser no longer sampling).
    if (this.inputLevel !== 0) {
      this.inputLevel = 0;
      this.callbacks.onInputLevel?.(0);
    }

    // Stop reacting to server voice notifications.
    this.realtime.onVoiceNewProducer(null);
    this.realtime.onVoiceProducerClosed(null);
    this.realtime.onVoiceActiveSpeakers(null);

    for (const peer of this.peers.values()) this.disposePeer(peer);
    this.peers.clear();
    this.consuming.clear();
    // Mic/transport gone → nobody is audible anymore; empty the HUD "People nearby" list.
    this.callbacks.onAudibleChange?.([]);

    // Webcam (ADR-0030): drop every received face bubble and tear down our own camera producer/stream.
    // The DESIRE flag (cameraDesired) is intentionally preserved so reinitialize can re-enable; permanent
    // stop() clears it explicitly. Stop tracks so no camera light lingers across a re-handshake.
    for (const vid of this.videoPeers.values()) {
      for (const t of vid.stream.getTracks()) t.stop();
      this.callbacks.onPeerVideoChange?.(vid.sessionId, null);
    }
    this.videoPeers.clear();
    this.videoBySession.clear();
    try {
      this.cameraProducer?.close();
    } catch {
      /* already closed */
    }
    this.cameraProducer = null;
    if (this.cameraTrack) {
      try {
        this.cameraTrack.stop();
      } catch {
        /* already stopped */
      }
      this.cameraTrack = null;
    }
    if (this.cameraStream) {
      for (const t of this.cameraStream.getTracks()) t.stop();
      this.cameraStream = null;
    }
    // The camera hardware is down now (reinitialize flips it back to 'on' if it re-enables).
    this.setCameraStatus('off');

    // Screen share (ADR-0033): drop every received screen + tear down our own. UNLIKE the camera, the
    // DESIRE flag is CLEARED — getDisplayMedia always needs a fresh user gesture, so a reconnect ends the
    // share (the host re-clicks "Share screen"). OfficePage notices status→'off' and stops the hub item.
    for (const scr of this.screenPeers.values()) {
      for (const t of scr.stream.getTracks()) t.stop();
      this.callbacks.onScreenShareChange?.(scr.sessionId, null);
    }
    this.screenPeers.clear();
    this.screenBySession.clear();
    for (const { el } of this.screenAudioEls.values()) {
      el.pause();
      el.srcObject = null;
    }
    this.screenAudioEls.clear();
    if (this.screenDesired || this.screenStream || this.screenVideoProducer) this.disableScreenshare();

    try {
      this.micProducer?.close();
    } catch {
      /* already closed */
    }
    this.micProducer = null;

    try {
      this.sendTransport?.close();
    } catch {
      /* already closed */
    }
    try {
      this.recvTransport?.close();
    } catch {
      /* already closed */
    }
    this.sendTransport = null;
    this.recvTransport = null;
    this.device = null;

    this.cleanupMedia();

    // The master fader node lives on the AudioContext we're about to close; drop the ref so a re-handshake
    // rebuilds it (the DESIRED level is a separate instance field, so it carries across the reconnect).
    this.masterVoiceGain = null;
    if (this.audioCtx) {
      void this.audioCtx.close().catch(() => undefined);
      this.audioCtx = null;
    }

    this.speakers.clear();
  }

  /** Tear everything down permanently (component unmount / logout). Idempotent. */
  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    // The zone poll runs from construction (independent of the mic) — only stop it on permanent teardown.
    if (this.zoneTimer) clearInterval(this.zoneTimer);
    this.zoneTimer = null;
    this.releaseResources();
    // Permanent teardown: the camera is genuinely off for good (releaseResources already set the status).
    this.cameraDesired = false;
    this.callbacks.onSpeakingChange?.(new Set());
    this.callbacks.onSpeakingLevels?.(new Map());
    this.setStatus('idle');
  }

  /**
   * A RECOVERABLE start() failure: release any half-built resources and reset `started` so the player
   * can retry from the HUD ("Allow mic" / "Retry voice" promise that retries work). Crucially does
   * NOT set `stopped` (that is only for permanent teardown), and never leaks the transports a partial
   * handshake created.
   */
  private failStart(status: VoiceStatus): void {
    this.releaseResources();
    this.started = false;
    this.callbacks.onSpeakingChange?.(new Set());
    this.callbacks.onSpeakingLevels?.(new Map());
    this.setStatus(status);
  }

  private cleanupMedia(): void {
    if (this.micTrack) {
      this.micTrack.stop();
      this.micTrack = null;
    }
    if (this.micStream) {
      for (const t of this.micStream.getTracks()) t.stop();
      this.micStream = null;
    }
  }
}
